import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Play, 
  BookOpen, 
  MessageSquare, 
  RotateCcw, 
  ChevronRight, 
  Sparkles,
  BarChart3,
  Gamepad2,
  Trophy,
  Bot,
  Edit3,
  Lock,
  Crown,
  Globe,
  Layers
} from 'lucide-react';
import Layout from '../components/layout/Layout';
import DailyGoalCard from '../components/common/DailyGoalCard';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import { COURSES } from '../i18n';

const HomePage = () => {
  const navigate = useNavigate();
  const { userProgress, tracks, reviewQueue } = useApp();
  const { user } = useAuth();

  // Get current course info
  const currentCourse = COURSES[user?.selected_course || 'pt-en'];

  // Find current lesson across all tracks
  const findCurrentLesson = () => {
    for (const track of tracks) {
      if (!track.locked && track.completedLessons < track.totalLessons) {
        return { track, lessonIndex: track.completedLessons + 1 };
      }
    }
    return null;
  };

  const currentLesson = findCurrentLesson();

  return (
    <Layout>
      <div className="px-4 py-6 space-y-6">
        {/* Welcome Section */}
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h1 className="font-display text-2xl font-bold">
              Olá, {user?.name?.split(' ')[0] || 'Estudante'}! 👋
            </h1>
            <p className="text-muted-foreground">
              Continue sua jornada no inglês
            </p>
          </div>
          {user?.is_admin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin')}
              className="shrink-0"
            >
              <Crown className="w-4 h-4 mr-1" />
              Admin
            </Button>
          )}
        </div>

        {/* Current Course Card */}
        <button
          onClick={() => navigate('/select-course')}
          className="w-full p-4 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 flex items-center justify-between group"
          data-testid="current-course-card"
        >
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-2xl">
              <span>{currentCourse?.flag?.split(' → ')[0]}</span>
              <span className="text-base text-muted-foreground">→</span>
              <span>{currentCourse?.flag?.split(' → ')[1]}</span>
            </div>
            <div className="text-left">
              <p className="font-semibold text-sm">{currentCourse?.nameNative || 'Inglês para Brasileiros'}</p>
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <Globe className="w-3 h-3" />
                Toque para trocar de curso
              </p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
        </button>

        {/* Daily Goal */}
        <DailyGoalCard />

        {/* Continue Learning Card */}
        {currentLesson && (
          <div className="card-elevated p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary" />
                <h2 className="font-semibold">Continue Aprendendo</h2>
              </div>
              <span className="text-xs text-muted-foreground">
                {currentLesson.track.icon} {currentLesson.track.title !== currentLesson.track.titlePt ? `${currentLesson.track.title} - ` : ''}{currentLesson.track.titlePt}
              </span>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Lição {currentLesson.lessonIndex} de {currentLesson.track.totalLessons}</span>
                <span className="font-medium">{Math.round((currentLesson.track.completedLessons / currentLesson.track.totalLessons) * 100)}%</span>
              </div>
              <Progress 
                value={(currentLesson.track.completedLessons / currentLesson.track.totalLessons) * 100} 
                className="h-2"
              />
            </div>

            <Button 
              className="w-full btn-primary-gradient"
              onClick={() => navigate(`/track/${currentLesson.track.id}`)}
            >
              <Play className="w-4 h-4 mr-2" />
              Continuar Trilha
            </Button>
          </div>
        )}

        {/* Modules Status - Show unlocked modules */}
        {user?.unlocked_modules && (
          <div className="card-elevated p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm">Seus Módulos</h3>
              <span className="text-xs text-muted-foreground">
                {user.unlocked_modules.length}/9 desbloqueados
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {['daily-life-1', 'daily-life-2', 'work-1', 'business-1'].map((moduleId, index) => {
                const isUnlocked = user.unlocked_modules.includes(moduleId);
                return (
                  <div
                    key={moduleId}
                    className={cn(
                      'px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1',
                      isUnlocked ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {isUnlocked ? '✓' : <Lock className="w-3 h-3" />}
                    Módulo {index + 1}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Actions - 2x4 Grid */}
        <div className="grid grid-cols-4 gap-3">
          {/* AI Chat */}
          <button
            onClick={() => navigate('/chat')}
            className="card-elevated p-3 text-center space-y-2 group"
            data-testid="quick-action-chat"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary-light flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
              <Bot className="w-5 h-5 text-primary-foreground" />
            </div>
            <p className="text-xs font-medium">Chat IA</p>
          </button>

          {/* Flashcards */}
          <button
            onClick={() => navigate('/flashcards')}
            className="card-elevated p-3 text-center space-y-2 group"
            data-testid="quick-action-flashcards"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
              <Layers className="w-5 h-5 text-white" />
            </div>
            <p className="text-xs font-medium">Flashcards</p>
          </button>

          {/* Games */}
          <button
            onClick={() => navigate('/games')}
            className="card-elevated p-3 text-center space-y-2 group"
            data-testid="quick-action-games"
          >
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
              <Gamepad2 className="w-5 h-5 text-accent" />
            </div>
            <p className="text-xs font-medium">Jogos</p>
          </button>

          {/* Review */}
          <button
            onClick={() => navigate('/review')}
            className="card-elevated p-3 text-center space-y-2 group relative"
            data-testid="quick-action-review"
          >
            <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
              <RotateCcw className="w-5 h-5 text-warning" />
            </div>
            <p className="text-xs font-medium">Revisão</p>
            {reviewQueue.length > 0 && (
              <span className="absolute top-1 right-1 w-5 h-5 bg-warning text-warning-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
                {reviewQueue.length}
              </span>
            )}
          </button>

          {/* Phrases */}
          <button
            onClick={() => navigate('/phrases')}
            className="card-elevated p-3 text-center space-y-2 group"
            data-testid="quick-action-phrases"
          >
            <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
              <MessageSquare className="w-5 h-5 text-success" />
            </div>
            <p className="text-xs font-medium">Frases</p>
          </button>

          {/* Writing */}
          <button
            onClick={() => navigate('/writing')}
            className="card-elevated p-3 text-center space-y-2 group"
            data-testid="quick-action-writing"
          >
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
              <Edit3 className="w-5 h-5 text-primary" />
            </div>
            <p className="text-xs font-medium">Escrita</p>
          </button>

          {/* Stats */}
          <button
            onClick={() => navigate('/stats')}
            className="card-elevated p-3 text-center space-y-2 group"
            data-testid="quick-action-stats"
          >
            <div className="w-10 h-10 rounded-xl bg-chart-1/10 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
              <BarChart3 className="w-5 h-5 text-chart-1" />
            </div>
            <p className="text-xs font-medium">Stats</p>
          </button>

          {/* Leaderboard */}
          <button
            onClick={() => navigate('/leaderboard')}
            className="card-elevated p-3 text-center space-y-2 group"
            data-testid="quick-action-leaderboard"
          >
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform">
              <Trophy className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-xs font-medium">Ranking</p>
          </button>
        </div>

        {/* Tracks Preview */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display font-semibold text-lg">Trilhas</h2>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/tracks')}
              className="text-primary"
            >
              Ver todas
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          <div className="space-y-3">
            {tracks.slice(0, 2).map(track => (
              <button
                key={track.id}
                onClick={() => navigate(`/track/${track.id}`)}
                className="card-elevated w-full p-4 text-left"
              >
                <div className="flex items-center gap-4">
                  <span className="text-3xl">{track.icon}</span>
                  <div className="flex-1">
                    <h3 className="font-semibold">{track.title}</h3>
                    <p className="text-sm text-muted-foreground">{track.titlePt}</p>
                    <div className="mt-2">
                      <Progress 
                        value={(track.completedLessons / track.totalLessons) * 100} 
                        className="h-1.5"
                      />
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Stats Summary */}
        <div className="card-elevated p-4">
          <h3 className="font-semibold mb-3">Seu Progresso</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-primary">{user?.lessons_completed?.length || userProgress.lessonsCompleted}</p>
              <p className="text-xs text-muted-foreground">Lições</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-accent">{user?.total_xp || userProgress.totalXP}</p>
              <p className="text-xs text-muted-foreground">XP Total</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-success">Nível {user?.level || userProgress.level}</p>
              <p className="text-xs text-muted-foreground">Atual</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HomePage;
